import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { bookingsAPI } from '../../../api/bookings';

export function usePaymentPage(bookingId) {
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Card details state
  const [cardDetails, setCardDetails] = useState({
    card_number: '',
    cardholder_name: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });

  const [errors, setErrors] = useState({});

  const loadBooking = useCallback(async () => {
    try {
      const data = await bookingsAPI.getBooking(bookingId);
      setBooking(data);
    } catch (error) {
      toast.error('Failed to load booking');
    }
  }, [bookingId]);

  useEffect(() => {
    loadBooking();
  }, [loadBooking]);

  // Card number formatting with spaces
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    const formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();
    return formatted.substring(0, 19); // Max 16 digits + 3 spaces
  };

  // Luhn algorithm for card validation
  const validateCardNumber = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (cleaned.length < 13 || cleaned.length > 19) return false;

    let sum = 0;
    let isEven = false;

    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i], 10);

      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }

      sum += digit;
      isEven = !isEven;
    }

    return sum % 10 === 0;
  };

  // Validate expiry date
  const validateExpiryDate = (month, year) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;

    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    if (monthNum < 1 || monthNum > 12) return false;
    if (yearNum < currentYear) return false;
    if (yearNum === currentYear && monthNum < currentMonth) return false;

    return true;
  };

  const handleCardInputChange = (field, value) => {
    let processedValue = value;

    if (field === 'card_number') {
      processedValue = formatCardNumber(value);
    } else if (field === 'expiry_month') {
      processedValue = value.replace(/\D/g, '').substring(0, 2);
    } else if (field === 'expiry_year') {
      processedValue = value.replace(/\D/g, '').substring(0, 4);
    } else if (field === 'cvv') {
      processedValue = value.replace(/\D/g, '').substring(0, 4);
    } else if (field === 'cardholder_name') {
      processedValue = value.toUpperCase();
    }

    setCardDetails(prev => ({
      ...prev,
      [field]: processedValue
    }));

    // Clear error for this field
    setErrors(prev => ({
      ...prev,
      [field]: null
    }));
  };

  // Fill test card data (for UAT environment)
  const fillTestCard = () => {
    setCardDetails({
      card_number: '4444 5555 6666 7777',
      cardholder_name: 'TEST CARD',
      expiry_month: '01',
      expiry_year: '2035',
      cvv: '555'
    });
    toast.info('Test card details filled');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!cardDetails.cardholder_name.trim()) {
      newErrors.cardholder_name = 'Cardholder name is required';
    }

    if (!validateCardNumber(cardDetails.card_number)) {
      newErrors.card_number = 'Invalid card number';
    }

    if (!cardDetails.expiry_month || !cardDetails.expiry_year) {
      newErrors.expiry = 'Expiry date is required';
    } else if (!validateExpiryDate(cardDetails.expiry_month, cardDetails.expiry_year)) {
      newErrors.expiry = 'Card has expired or invalid date';
    }

    if (!cardDetails.cvv || cardDetails.cvv.length < 3) {
      newErrors.cvv = 'Invalid CVV';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInitiatePayment = async () => {
    setProcessing(true);
    try {
      // Step 1 & 2: Initiate payment (getNewOrderId + hash)
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/payment`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      // IMPORTANT: Check status BEFORE parsing JSON to avoid consuming body twice
      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          // If response is not JSON, create error object
          errorData = {
            detail: {
              error: 'Server Error',
              code: response.status,
              message: response.statusText || 'Payment initiation failed'
            }
          };
        }

        // Format error message for display
        const errorDetail = errorData.detail || errorData;
        let errorMessage = '';

        if (typeof errorDetail === 'string') {
          errorMessage = errorDetail;
        } else if (errorDetail.message) {
          errorMessage = `${errorDetail.error || 'Payment Error'}\n\nError Code: ${errorDetail.code}\nMessage: ${errorDetail.message}`;
          if (errorDetail.details) {
            errorMessage += `\n\nDetails: ${JSON.stringify(errorDetail.details, null, 2)}`;
          }
        } else {
          errorMessage = 'Payment initiation failed. Please try again.';
        }

        throw new Error(errorMessage);
      }

      // Now safe to parse success response
      const data = await response.json();

      if (data.status === 'payment_ready' && data.payment_url) {
        // Pocket Pay returns a payment URL - redirect user to complete payment
        toast.success('Redirecting to secure payment page...', { duration: 2000 });

        // Redirect to Pocket Pay payment page after short delay
        setTimeout(() => {
          window.location.href = data.payment_url;
        }, 1500);
      } else if (data.status === 'initiated') {
        // Fallback for old flow
        setPaymentData(data);
        setPaymentInitiated(true);
        toast.success('Payment initiated. Please enter your card details.');
      } else {
        throw new Error(data.message || 'Payment initiation failed');
      }
    } catch (error) {
      console.error('Payment initiation error:', error);

      // Handle capacity error specifically
      if (error.message && error.message.includes('Courts no longer available')) {
        toast.error(error.message, { duration: 6000 });
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        // Show detailed error to user
        toast.error(error.message || 'Failed to initiate payment', {
          duration: 10000,
          style: { whiteSpace: 'pre-wrap' }
        });
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleProcessPayment = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setProcessing(true);

    try {
      // Step 3: Process payment with card details
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/payment/process`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          card_number: cardDetails.card_number.replace(/\s/g, ''),
          cardholder_name: cardDetails.cardholder_name,
          expiry_month: cardDetails.expiry_month,
          expiry_year: cardDetails.expiry_year,
          cvv: cardDetails.cvv
        })
      });

      // IMPORTANT: Check status BEFORE parsing JSON
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Payment processing failed');
      }

      // Now safe to parse success response
      const data = await response.json();

      if (data.status === 'success') {
        toast.success('Payment successful!');
        navigate(`/booking-confirmed/${bookingId}`);
      } else {
        throw new Error(data.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      toast.error(error.message || 'Payment processing failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return {
    booking, processing, paymentInitiated, paymentData, cardDetails, errors,
    handleCardInputChange, fillTestCard, handleInitiatePayment, handleProcessPayment,
  };
}
